import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * Toast — notification transitoire non-bloquante
 *
 * Remplace les patterns DIY `setToast({ ok, text })` et les `alert()` natifs.
 *
 * Usage :
 *   // 1. Wrap l'app avec le provider (une seule fois, dans main.tsx)
 *   <ToastProvider>
 *     <App />
 *   </ToastProvider>
 *
 *   // 2. Dans un composant, appeler le hook :
 *   const toast = useToast()
 *   toast.success('Client ajoute')
 *   toast.error('Erreur lors de la sauvegarde : ' + err.message)
 *   toast.info('Nouvelle version disponible')
 *   toast.warning('Connexion instable')
 *
 *   // 3. Options avancees :
 *   toast.show({ intent: 'success', title: 'Ok', description: 'Detail...', duration: 6000 })
 *   const id = toast.info('Processing...', { duration: Infinity })
 *   toast.dismiss(id)
 *
 * A11y : role="status" aria-live="polite" sur la region. Chaque toast est un
 * element role="alert" pour les erreurs, role="status" pour le reste.
 */

// ─────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────

export type ToastIntent = 'success' | 'error' | 'warning' | 'info'

export interface ToastOptions {
  /** Duree avant auto-dismiss en ms. Defaut : 4000. Passer Infinity pour bloquer. */
  duration?: number
  /** Description optionnelle sous le titre. */
  description?: string
  /** Callback quand le toast est ferme (auto ou manuel). */
  onDismiss?: () => void
  /** Bouton d'action optionnel (ex. « Annuler » pour un undo). Clic = onClick + dismiss. */
  action?: { label: string; onClick: () => void }
}

export interface ToastShowOptions extends ToastOptions {
  intent?: ToastIntent
  title: string
}

interface ToastInstance {
  id: string
  intent: ToastIntent
  title: string
  description?: string
  duration: number
  onDismiss?: () => void
  action?: { label: string; onClick: () => void }
  createdAt: number
}

export interface ToastApi {
  /** Shortcut toast.success(title, opts) */
  success: (title: string, opts?: ToastOptions) => string
  /** Shortcut toast.error(title, opts). Par defaut duration 6000ms (plus long). */
  error: (title: string, opts?: ToastOptions) => string
  /** Shortcut toast.warning(title, opts). */
  warning: (title: string, opts?: ToastOptions) => string
  /** Shortcut toast.info(title, opts). */
  info: (title: string, opts?: ToastOptions) => string
  /** Appel complet avec toutes les options. */
  show: (opts: ToastShowOptions) => string
  /** Ferme un toast par id. */
  dismiss: (id: string) => void
  /** Ferme tous les toasts. */
  dismissAll: () => void
}

// ─────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────

const ToastContext = React.createContext<ToastApi | null>(null)

/**
 * Hook pour acceder a l'API Toast. Doit etre utilise sous un <ToastProvider>.
 * Fallback safe (no-op + warn) si utilise hors provider pour ne pas crasher l'app.
 */
export function useToast(): ToastApi {
  const ctx = React.useContext(ToastContext)
  if (ctx) return ctx
  // Fallback no-op pour les tests / composants isoles
  if (typeof window !== 'undefined') {
    console.warn('[useToast] appele hors de <ToastProvider> — no-op')
  }
  const noop = () => ''
  return {
    success: noop,
    error: noop,
    warning: noop,
    info: noop,
    show: noop,
    dismiss: () => {},
    dismissAll: () => {},
  }
}

// ─────────────────────────────────────────────────────────────────────
// Icons (inline SVG pour zero-dependency)
// ─────────────────────────────────────────────────────────────────────

function ToastIcon({ intent }: { intent: ToastIntent }) {
  const common = { className: 'w-5 h-5 shrink-0', fill: 'none', viewBox: '0 0 24 24', strokeWidth: 2, stroke: 'currentColor', 'aria-hidden': true as const }
  switch (intent) {
    case 'success':
      return (
        <svg {...common}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
      )
    case 'error':
      return (
        <svg {...common}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" /></svg>
      )
    case 'warning':
      return (
        <svg {...common}><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" /></svg>
      )
    case 'info':
    default:
      return (
        <svg {...common}><path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" /></svg>
      )
  }
}

// ─────────────────────────────────────────────────────────────────────
// Variants
// ─────────────────────────────────────────────────────────────────────

const toastVariants = cva(
  'pointer-events-auto flex items-start gap-3 p-3.5 pr-8 rounded-lg border shadow-md bg-surface-card text-ink relative min-w-[280px] max-w-[400px] animate-in slide-in-from-right-4 fade-in duration-200',
  {
    variants: {
      intent: {
        success: 'border-success/30',
        error: 'border-error/30',
        warning: 'border-warning/30',
        info: 'border-info/30',
      },
    },
    defaultVariants: { intent: 'info' },
  }
)

const iconColorByIntent: Record<ToastIntent, string> = {
  success: 'text-success',
  error: 'text-error',
  warning: 'text-warning',
  info: 'text-info',
}

// ─────────────────────────────────────────────────────────────────────
// ToastItem
// ─────────────────────────────────────────────────────────────────────

interface ToastItemProps extends VariantProps<typeof toastVariants> {
  toast: ToastInstance
  onDismiss: (id: string) => void
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const { id, intent, title, description, duration, action } = toast

  React.useEffect(() => {
    if (!isFinite(duration)) return
    const t = setTimeout(() => onDismiss(id), duration)
    return () => clearTimeout(t)
  }, [id, duration, onDismiss])

  const isAlert = intent === 'error' || intent === 'warning'

  return (
    <div
      className={cn(toastVariants({ intent }))}
      role={isAlert ? 'alert' : 'status'}
      aria-live={isAlert ? 'assertive' : 'polite'}
    >
      <span className={cn(iconColorByIntent[intent], 'mt-0.5')}>
        <ToastIcon intent={intent} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-data font-medium leading-snug break-words">{title}</p>
        {description ? (
          <p className="text-caption text-ink-muted mt-0.5 leading-snug break-words">{description}</p>
        ) : null}
        {action ? (
          <button
            type="button"
            onClick={() => { action.onClick(); onDismiss(id) }}
            className="mt-1.5 text-caption font-semibold text-violet-500 hover:text-violet-600 underline underline-offset-2 cursor-pointer"
          >
            {action.label}
          </button>
        ) : null}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(id)}
        className="absolute top-2 right-2 p-1 rounded-md text-ink-muted hover:text-ink-secondary hover:bg-surface-low focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer transition-colors"
        aria-label="Fermer la notification"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────
// ToastProvider
// ─────────────────────────────────────────────────────────────────────

export interface ToastProviderProps {
  children: React.ReactNode
  /** Duree par defaut pour les toasts en ms. Defaut : 4000. */
  defaultDuration?: number
  /** Duree par defaut pour les erreurs. Defaut : 6000 (lecture plus longue). */
  errorDuration?: number
  /** Nombre max de toasts simultanes. Defaut : 5. Les plus anciens sont evinces. */
  maxToasts?: number
}

let idCounter = 0
const nextId = () => `toast-${Date.now()}-${idCounter++}`

export function ToastProvider({
  children,
  defaultDuration = 4000,
  errorDuration = 6000,
  maxToasts = 5,
}: ToastProviderProps) {
  const [toasts, setToasts] = React.useState<ToastInstance[]>([])

  const dismiss = React.useCallback((id: string) => {
    setToasts(prev => {
      const t = prev.find(t => t.id === id)
      if (t?.onDismiss) t.onDismiss()
      return prev.filter(t => t.id !== id)
    })
  }, [])

  const dismissAll = React.useCallback(() => {
    setToasts(prev => {
      prev.forEach(t => t.onDismiss?.())
      return []
    })
  }, [])

  const show = React.useCallback((opts: ToastShowOptions): string => {
    const id = nextId()
    const intent = opts.intent ?? 'info'
    const duration =
      opts.duration !== undefined
        ? opts.duration
        : intent === 'error'
        ? errorDuration
        : defaultDuration

    const instance: ToastInstance = {
      id,
      intent,
      title: opts.title,
      description: opts.description,
      duration,
      onDismiss: opts.onDismiss,
      action: opts.action,
      createdAt: Date.now(),
    }

    setToasts(prev => {
      const next = [...prev, instance]
      if (next.length > maxToasts) {
        // Evicte les plus anciens
        return next.slice(-maxToasts)
      }
      return next
    })

    return id
  }, [defaultDuration, errorDuration, maxToasts])

  const api = React.useMemo<ToastApi>(() => ({
    success: (title, opts) => show({ ...opts, intent: 'success', title }),
    error: (title, opts) => show({ ...opts, intent: 'error', title }),
    warning: (title, opts) => show({ ...opts, intent: 'warning', title }),
    info: (title, opts) => show({ ...opts, intent: 'info', title }),
    show,
    dismiss,
    dismissAll,
  }), [show, dismiss, dismissAll])

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

// ─────────────────────────────────────────────────────────────────────
// ToastViewport — zone de rendu des toasts empiles
// ─────────────────────────────────────────────────────────────────────

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastInstance[]
  onDismiss: (id: string) => void
}) {
  if (toasts.length === 0) return null

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 z-[9999] flex flex-col gap-2 max-w-[calc(100vw-2rem)]"
      role="region"
      aria-label="Notifications"
    >
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  )
}
